import { LibrarySection } from '@lib/utils/librarySections';
import { ReactNode } from 'react';
import { LibrarySectionLabel } from './LibrarySectionLabel';

/**
 * Interleaves section headers with their items into **one flat array of children**, for the two
 * React surfaces that render sections (`LibraryCards`, `LibraryList`).
 *
 * Returning a flat array is the point, not a detail. Keys are scoped to their parent, so
 * wrapping each section in a keyed fragment would make the section *shape* part of React's
 * reconciliation identity: gaining a first pin swaps one fragment for two and remounts every
 * card, resetting `useLibraryThumbnail`'s fallback walk and churning the image cache on the exact
 * transition pinning causes. Flat, the items keep matching on their own keys while a label
 * element simply appears beside them.
 *
 * That invariant used to be duplicated in both surfaces and enforced by a comment. Here it is
 * enforced by the return type.
 *
 * `renderItem` must key its own element.
 */
export function renderLibrarySections<T>(
  sections: LibrarySection<T>[],
  {
    labelClassName,
    renderItem,
  }: {
    labelClassName?: string;
    renderItem: (item: T) => ReactNode;
  },
): ReactNode[] {
  return sections.flatMap((section) => [
    ...(section.id
      ? [
          <LibrarySectionLabel
            key={`section:${section.id}`}
            id={section.id}
            className={labelClassName}
          />,
        ]
      : []),
    ...section.items.map(renderItem),
  ]);
}
